import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';

export type ConfirmModalType = 'warning' | 'danger' | 'info' | 'success';

export interface ConfirmModalProps {
  /** 是否显示弹窗 */
  open: boolean;
  /** 标题 */
  title?: React.ReactNode;
  /** 描述文案或复杂内容 */
  content?: React.ReactNode;
  /** 弹窗类型（影响图标及按纽默认样式） */
  type?: ConfirmModalType;
  /** 自定义图标类名或 React 节点 (如 'ri-error-warning-line') */
  icon?: React.ReactNode;
  /** 确认按钮文字 */
  confirmText?: string;
  /** 取消按钮文字 */
  cancelText?: string;
  /** 点击确认按钮回调 */
  onConfirm?: () => void;
  /** 点击取消按钮回调 */
  onCancel?: () => void;
  /** 确认按钮是否处于加载状态 */
  loading?: boolean;
  /** 点击遮罩层是否触发 cancel (默认 true) */
  closeOnOverlayClick?: boolean;
  /** ESC 键是否触发 cancel (默认 true) */
  closeOnEsc?: boolean;
  /** 堆叠层级 (默认 10000) */
  zIndex?: number;
}

const TYPE_THEMES: Record<
  ConfirmModalType,
  {
    iconBg: string;
    iconColor: string;
    defaultIcon: string;
    confirmBtn: string;
  }
> = {
  warning: {
    iconBg: 'bg-amber-100',
    iconColor: 'text-amber-600',
    defaultIcon: 'ri-error-warning-line',
    confirmBtn: 'bg-primary-500 hover:bg-primary-600 active:bg-primary-700 text-white',
  },
  danger: {
    iconBg: 'bg-red-100',
    iconColor: 'text-red-600',
    defaultIcon: 'ri-error-warning-line',
    confirmBtn: 'bg-red-500 hover:bg-red-600 active:bg-red-700 text-white',
  },
  info: {
    iconBg: 'bg-blue-100',
    iconColor: 'text-blue-600',
    defaultIcon: 'ri-information-line',
    confirmBtn: 'bg-primary-500 hover:bg-primary-600 active:bg-primary-700 text-white',
  },
  success: {
    iconBg: 'bg-emerald-100',
    iconColor: 'text-emerald-600',
    defaultIcon: 'ri-checkbox-circle-line',
    confirmBtn: 'bg-emerald-500 hover:bg-emerald-600 active:bg-emerald-700 text-white',
  },
};

export default function ConfirmModal({
  open,
  title = '提示',
  content,
  type = 'warning',
  icon,
  confirmText = '确定',
  cancelText = '取消',
  onConfirm,
  onCancel,
  loading = false,
  closeOnOverlayClick = true,
  closeOnEsc = true,
  zIndex = 10000,
}: ConfirmModalProps) {
  const [visible, setVisible] = useState(false);
  const [animating, setAnimating] = useState(false);

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;
    if (open) {
      setVisible(true);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setAnimating(true);
        });
      });
    } else {
      setAnimating(false);
      timer = setTimeout(() => {
        setVisible(false);
      }, 250); // 匹配 transition-all duration-250
    }
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [open]);

  useEffect(() => {
    if (!open || !visible || !closeOnEsc) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onCancel?.();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [open, visible, closeOnEsc, onCancel]);

  if (!visible) return null;

  const theme = TYPE_THEMES[type] || TYPE_THEMES.warning;

  const renderIcon = () => {
    if (!icon && !theme.defaultIcon) return null;
    if (typeof icon === 'string' || !icon) {
      const iconClass = (typeof icon === 'string' && icon) ? icon : theme.defaultIcon;
      return <i className={`${iconClass} text-2xl`}></i>;
    }
    return icon;
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && closeOnOverlayClick && !loading) {
      onCancel?.();
    }
  };

  return createPortal(
    <div
      style={{ zIndex }}
      onClick={handleOverlayClick}
      className={`fixed inset-0 flex items-center justify-center p-4 transition-all duration-250 ease-out ${
        animating ? 'bg-black/60 backdrop-blur-sm opacity-100' : 'bg-black/0 backdrop-blur-none opacity-0 pointer-events-none'
      }`}
    >
      <div
        className={`w-full max-w-sm bg-white/95 backdrop-blur-sm rounded-2xl p-6 shadow-2xl border border-white/20 text-center transition-all duration-250 ease-out transform ${
          animating ? 'translate-y-0 opacity-100 scale-100' : 'translate-y-4 opacity-0 scale-95'
        }`}
      >
        {/* 图标区 */}
        <div className={`w-12 h-12 mx-auto mb-4 rounded-full ${theme.iconBg} ${theme.iconColor} flex items-center justify-center shadow-xs`}>
          {renderIcon()}
        </div>

        {/* 标题 */}
        {title && <h3 className="text-base font-bold text-foreground-950 mb-1.5">{title}</h3>}

        {/* 内容描述 */}
        {content && <div className="text-xs text-foreground-500 mb-6 leading-relaxed">{content}</div>}

        {/* 按钮组 */}
        <div className="flex gap-3">
          {cancelText && (
            <button
              type="button"
              onClick={onCancel}
              disabled={loading}
              className="flex-1 py-2.5 rounded-xl border border-gray-300/80 bg-white text-foreground-700 text-xs font-medium hover:bg-gray-50 active:bg-gray-100 transition-all cursor-pointer shadow-xs disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {cancelText}
            </button>
          )}

          {confirmText && (
            <button
              type="button"
              onClick={onConfirm}
              disabled={loading}
              className={`flex-1 py-2.5 rounded-xl text-xs font-semibold transition-all cursor-pointer shadow-sm flex items-center justify-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed ${theme.confirmBtn}`}
            >
              {loading ? (
                <>
                  <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  <span>处理中...</span>
                </>
              ) : (
                <span>{confirmText}</span>
              )}
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
